import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Home, Mail, Lock, User, Phone, Eye, EyeOff, Loader2, ArrowLeft, AlertCircle
} from 'lucide-react';
import { signInWithGoogle, fetchFirebaseConfig } from '@/lib/firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return') || '/marketplace';
  
  const [mode, setMode] = useState('login'); // login | signup
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [firebaseEnabled, setFirebaseEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  
  // Check Firebase config
  useEffect(() => {
    fetchFirebaseConfig().then(cfg => setFirebaseEnabled(!!cfg));
  }, []);
  
  // Check if already logged in as guest
  useEffect(() => {
    const token = localStorage.getItem('guest_token');
    if (token) {
      // Verify token is still valid
      fetch(`${BACKEND_URL}/api/marketplace/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(resp => {
        if (resp.ok) {
          navigate(returnUrl);
        } else {
          localStorage.removeItem('guest_token');
          localStorage.removeItem('guest_user');
        }
      }).catch(() => {
        localStorage.removeItem('guest_token');
        localStorage.removeItem('guest_user');
      });
    }
  }, [navigate, returnUrl]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = mode === 'login' 
        ? '/api/marketplace/auth/login'
        : '/api/marketplace/auth/signup';
      
      const body = mode === 'login'
        ? { email: form.email, password: form.password }
        : {
            email: form.email,
            password: form.password,
            first_name: form.firstName,
            last_name: form.lastName,
            phone: form.phone || null
          };
      
      const resp = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }
      
      // Store token and user info
      localStorage.setItem('guest_token', data.token);
      localStorage.setItem('guest_user', JSON.stringify(data.guest));
      
      // Redirect to return URL
      navigate(returnUrl);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      const result = await signInWithGoogle();
      // Send Firebase ID token to guest Google callback
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/auth/google/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: result.idToken }),
      });
      const data = await resp.json();
      if (data.token) {
        localStorage.setItem('guest_token', data.token);
        localStorage.setItem('guest_user', JSON.stringify(data.user));
        navigate(returnUrl, { replace: true });
      } else {
        setError(data.detail || 'Google sign-in failed');
      }
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setGoogleLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/marketplace')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to iStayBnB</span>
          </button>
          
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/marketplace')}>
            <div className="w-10 h-10 bg-gradient-to-br from-[#FF385C] to-[#E31C5F] rounded-xl flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">iStayBnB</span>
          </div>
          
          <div className="w-24" /> {/* Spacer */}
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
              {mode === 'login' ? 'Welcome back' : 'Create an account'}
            </h1>
            <p className="text-center text-gray-500 mb-8">
              {mode === 'login' 
                ? 'Sign in to book your next stay' 
                : 'Join iStayBnB to discover unique stays'}
            </p>
            
            {/* Google Login Button */}
            {firebaseEnabled && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="w-full h-12 rounded-xl border-gray-300 hover:bg-gray-50 mb-4"
                >
                  {googleLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </Button>
                
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">or</span>
                  </div>
                </div>
              </>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-xl flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">First name</Label>
                    <div className="relative mt-1">
                      <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="firstName"
                        type="text"
                        required
                        value={form.firstName}
                        onChange={(e) => setForm({...form, firstName: e.target.value})}
                        className="pl-10 h-11 rounded-xl"
                        placeholder="John"
                        data-testid="guest-firstname"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Last name</Label>
                    <div className="relative mt-1">
                      <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="lastName"
                        type="text"
                        required
                        value={form.lastName}
                        onChange={(e) => setForm({...form, lastName: e.target.value})}
                        className="pl-10 h-11 rounded-xl"
                        placeholder="Doe"
                        data-testid="guest-lastname"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="pl-10 h-11 rounded-xl"
                    placeholder="you@example.com"
                    data-testid="guest-email"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={mode === 'signup' ? 8 : undefined}
                    value={form.password}
                    onChange={(e) => setForm({...form, password: e.target.value})}
                    className="pl-10 pr-10 h-11 rounded-xl"
                    placeholder={mode === 'signup' ? 'Min. 8 characters' : '••••••••'}
                    data-testid="guest-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {mode === 'signup' && (
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Phone <span className="text-gray-400">(optional)</span>
                  </Label>
                  <div className="relative mt-1">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className="pl-10 h-11 rounded-xl"
                      placeholder="+254 712 345 678"
                      data-testid="guest-phone"
                    />
                  </div>
                </div>
              )}
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#FF385C] to-[#E31C5F] hover:from-[#E31C5F] hover:to-[#C91848] text-white font-semibold"
                data-testid="guest-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : mode === 'login' ? (
                  'Sign in'
                ) : (
                  'Create account'
                )}
              </Button>
            </form>
            
            {/* Toggle Mode */}
            <p className="mt-6 text-center text-sm text-gray-600">
              {mode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button 
                    onClick={() => { setMode('signup'); setError(null); }}
                    className="text-[#FF385C] font-semibold hover:underline"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button 
                    onClick={() => { setMode('login'); setError(null); }}
                    className="text-[#FF385C] font-semibold hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
            
            {/* Host Login Link */}
            <p className="mt-4 text-center text-xs text-gray-400">
              Are you a property host?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:underline"
              >
                Host login
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
